
import React, { useEffect } from 'react';
import SafeBulletinContent from './SafeBulletinContent';
import BulletinVideoPreview from './BulletinVideoPreview';
import BulletinImage from './BulletinImage';
import { cn } from '@/lib/utils';

interface BulletinMediaContentProps {
  bulletin: any;
  size: string;
  imageError: boolean;
  imageLoaded: boolean;
  onImageLoad: () => void;
  onImageError: () => void;
  isSelected: boolean;
  isHovered: boolean;
  getTextPosition: () => "center" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
  onDoubleClick: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  isMature: boolean;
  showMatureContent: boolean;
}

const BulletinMediaContent: React.FC<BulletinMediaContentProps> = ({
  bulletin,
  size,
  imageError,
  imageLoaded,
  onImageLoad,
  onImageError,
  isSelected,
  isHovered,
  getTextPosition,
  onDoubleClick,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onClick,
  isMature,
  showMatureContent
}) => {
  // Check if we have valid media to display
  const hasValidMedia = bulletin.image_url && !imageError;
  
  // Detect if this is a video file
  const isVideo = bulletin.file_type === 'mp4' || 
                  (bulletin.image_url && (
                    bulletin.image_url.includes('.mp4') || 
                    bulletin.image_url.includes('.webm') || 
                    bulletin.image_url.includes('.mov')
                  ));
  
  // Check if there's actual text content to display
  const hasTextContent = bulletin.content && bulletin.content.trim().length > 0;
  
  // Get focal point values with fallbacks to center (50%)
  const focalPointX = bulletin.focal_point_x ?? 50;
  const focalPointY = bulletin.focal_point_y ?? 50;
  
  // Always use focal points, even when selected
  // Only switch to center for actual fullscreen viewing  
  const objectPosition = `${focalPointX}% ${focalPointY}%`;
  
  // Preload image at full resolution when selected for detail view
  useEffect(() => {
    if (isSelected && bulletin.image_url && !imageLoaded && !isVideo) {
      const img = new Image();
      img.src = bulletin.image_url;
      img.onload = onImageLoad;
      img.onerror = onImageError;
      
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }
  }, [isSelected, bulletin.image_url, imageLoaded, onImageLoad, onImageError, isVideo]);


  // For text-only posts, ensure the container has proper visual presence
  const isTextOnlyPost = hasTextContent && !hasValidMedia;

  // Check if click is in the like corner (bottom-right 48px area)
  const isClickInLikeCorner = (e: React.MouseEvent | React.TouchEvent) => {
    const target = e.currentTarget as HTMLElement;
    if (!target) return false;
    
    const rect = target.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    // Check if click is in bottom-right 48px corner (larger for mobile)
    const cornerSize = 48;
    return (x >= rect.width - cornerSize && y >= rect.height - cornerSize);
  };

  return (
    <div 
      className={cn(
        "relative w-full h-full select-none bulletin-no-select rounded-xl",
        // Add minimum height and background for text-only posts
        isTextOnlyPost && "min-h-[120px] bg-gradient-to-br from-card via-card to-muted"
      )}
      style={{ 
        zIndex: 0,
        touchAction: 'pan-y', // Lock horizontal swipes to avoid lateral wobble while still permitting vertical scroll
        userSelect: 'none',
        WebkitUserSelect: 'none'
      } as React.CSSProperties & { WebkitUserSelect?: string; WebkitTouchCallout?: string; WebkitTapHighlightColor?: string }}
      onDoubleClick={(e) => {
        e.preventDefault(); // Prevent double-tap zoom on mobile
        onDoubleClick(e);
      }}
      onClick={(e) => {
        // Check if click is in like corner first
        if (isClickInLikeCorner(e)) {
          console.log('[MediaContent] Click in like corner, ignoring');
          return;
        }
        
        // Handle single click/tap event
        if (onClick) {
          e.preventDefault();
          onClick(e);
        }
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Touch protection overlay for images */}
      {hasValidMedia && (
        <div className="touch-protection-overlay" />
      )}

      {/* Render text content overlay only in inspect view */}
      {hasTextContent && isSelected && (
        <SafeBulletinContent 
          content={bulletin.content} 
          size={size === 'horizontal' ? 'large' : 'medium'} 
          position={getTextPosition()} 
          hasImage={hasValidMedia}
        />
      )}

      {/* Render media if available */}
      {hasValidMedia && (
        <>
          {isVideo ? (
            <BulletinVideoPreview
              src={bulletin.image_url}
              isSelected={isSelected}
              onLoadedData={onImageLoad}
              onError={onImageError}
              isMature={isMature}
              showMatureContent={showMatureContent}
            />
           ) : (
              <BulletinImage
                src={bulletin.image_url}
                fileType={bulletin.file_type}
                isSelected={isSelected}
                onClick={() => {}} // Click is handled by parent container
                onLoad={onImageLoad}
                onError={onImageError}
                isMature={isMature}
                showMatureContent={showMatureContent}
                size={size === 'horizontal' ? 'horizontal' : isSelected ? 'large' : 'medium'}
                focalPointX={focalPointX}
                focalPointY={focalPointY}
              />
           )}
        </>
      )}
    </div>
  );
};

export default BulletinMediaContent;
